/* VFW photo upload — <vfw-photo-upload> progressive enhancement for the
   contact form (Fitment Help + Get Featured). Each chosen photo uploads
   immediately to the VFW upload service (HEIC conversion + compression
   happen server-side), shows its own progress bar, and lands as a URL in
   the hidden contact[Photo links] field so the native Shopify contact
   email carries clickable links. No JS → the form still submits fine,
   just without photos (the field explains itself). */
(function () {
  'use strict';

  const ENDPOINT = 'https://vfw-uploads.vercel.app/api/upload';
  const MAX_BYTES = 20 * 1024 * 1024;

  class VfwPhotoUpload extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';
      this.max = parseInt(this.getAttribute('max') || '5', 10);
      this.formKind = this.getAttribute('form-kind') || 'contact';
      this.items = [];
      this.render();
    }

    get form() { return this.closest('form'); }
    get hiddenField() { return this.querySelector('[data-vfw-pu-field]'); }

    render() {
      const addLabel = this.getAttribute('add-label') || 'Add photos';
      const hint = this.getAttribute('hint') || '';
      this.innerHTML =
        '<div class="vfw-pu__grid" data-vfw-pu-grid></div>' +
        '<button type="button" class="vfw-pu__add" data-vfw-pu-add>' +
        '<span class="vfw-pu__add-plus" aria-hidden="true">+</span>' +
        '<span>' + addLabel + '</span></button>' +
        (hint ? '<p class="vfw-pu__hint">' + hint + '</p>' : '') +
        '<p class="vfw-pu__error" data-vfw-pu-error role="alert" hidden></p>' +
        '<input type="hidden" name="contact[Photo links]" value="" data-vfw-pu-field>' +
        '<input type="file" accept="image/*,.heic,.heif" multiple hidden data-vfw-pu-input>';
      this.querySelector('[data-vfw-pu-add]').addEventListener('click', () => {
        this.querySelector('[data-vfw-pu-input]').click();
      });
      this.querySelector('[data-vfw-pu-input]').addEventListener('change', (e) => {
        this.addFiles(Array.from(e.target.files || []));
        e.target.value = '';
      });
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          if (this.items.some((i) => i.state === 'uploading')) {
            e.preventDefault();
            this.say(this.getAttribute('wait-msg') || 'One moment — your photos are still uploading.');
          }
        });
      }
    }

    say(msg) {
      const el = this.querySelector('[data-vfw-pu-error]');
      if (!msg) { el.hidden = true; el.textContent = ''; return; }
      el.textContent = msg;
      el.hidden = false;
    }

    addFiles(files) {
      this.say('');
      for (const file of files) {
        if (this.items.filter((i) => i.state !== 'failed').length >= this.max) {
          this.say((this.getAttribute('max-msg') || 'Up to {n} photos.').replace('{n}', this.max));
          break;
        }
        if (file.size > MAX_BYTES) {
          this.say((this.getAttribute('size-msg') || '"{f}" is over 20 MB — please choose a smaller photo.').replace('{f}', file.name));
          continue;
        }
        this.startUpload(file);
      }
    }

    /* Vercel functions reject request bodies over ~4.5 MB
       (FUNCTION_PAYLOAD_TOO_LARGE — caught in the 2026-07-30 stress test),
       so big sources are shrunk CLIENT-side before they travel: decode,
       fit inside 2400px, re-encode JPEG. Safari decodes HEIC natively —
       which is exactly where HEIC files come from. Files that cannot be
       decoded pass through raw when small enough; otherwise the user gets
       a plain-language error instead of a silent failure. */
    prepareFile(file) {
      const RAW_LIMIT = 3.5 * 1024 * 1024;
      const decode = ('createImageBitmap' in window)
        ? createImageBitmap(file)
        : Promise.reject(new Error('no-bitmap'));
      return decode.then((bmp) => {
        const scale = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
        return new Promise((resolve, reject) => {
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0 && blob.size < RAW_LIMIT) resolve(blob);
            else if (file.size <= RAW_LIMIT) resolve(file);
            else reject(new Error('too-big'));
          }, 'image/jpeg', 0.85);
        });
      }).catch((e) => {
        if (e && e.message === 'too-big') throw e;
        if (file.size <= RAW_LIMIT) return file;
        throw new Error('too-big');
      });
    }

    startUpload(file) {
      const item = { file, state: 'uploading', url: '', el: null, xhr: null };
      this.items.push(item);
      const tile = document.createElement('div');
      tile.className = 'vfw-pu__tile';
      tile.innerHTML =
        '<img class="vfw-pu__thumb" alt="" hidden>' +
        '<span class="vfw-pu__thumb-fallback" aria-hidden="true">📷</span>' +
        '<span class="vfw-pu__bar"><span class="vfw-pu__bar-fill" style="width:0%"></span></span>' +
        '<button type="button" class="vfw-pu__remove" aria-label="Remove photo">&times;</button>';
      item.el = tile;
      this.querySelector('[data-vfw-pu-grid]').appendChild(tile);

      // Thumbnail (HEIC won't decode in most browsers — the camera glyph stands in)
      const url = URL.createObjectURL(file);
      const img = tile.querySelector('.vfw-pu__thumb');
      img.onload = () => { img.hidden = false; tile.querySelector('.vfw-pu__thumb-fallback').hidden = true; };
      img.src = url;

      tile.querySelector('.vfw-pu__remove').addEventListener('click', () => {
        if (item.xhr && item.state === 'uploading') item.xhr.abort();
        item.state = 'removed';
        tile.remove();
        URL.revokeObjectURL(url);
        this.sync();
      });

      this.prepareFile(file).then((payload) => {
        if (item.state === 'removed') return;
        this.sendPayload(item, tile, payload);
      }).catch(() => {
        item.state = 'failed';
        tile.classList.add('is-failed');
        this.say(this.getAttribute('size-msg') || ('"' + file.name + '" could not be processed — please choose a smaller photo.'));
        this.sync();
      });
      this.sync();
    }

    sendPayload(item, tile, payload) {
      const xhr = new XMLHttpRequest();
      item.xhr = xhr;
      xhr.open('POST', ENDPOINT, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.setRequestHeader('X-VFW-Form', this.formKind);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          tile.querySelector('.vfw-pu__bar-fill').style.width = Math.round((e.loaded / e.total) * 92) + '%';
        }
      };
      xhr.onload = () => {
        let ok = false;
        try {
          const res = JSON.parse(xhr.responseText || '{}');
          if (xhr.status === 200 && res.url) {
            item.url = res.url;
            item.state = 'done';
            ok = true;
          } else if (res.error) {
            this.say(res.error);
          }
        } catch (_) { /* fall through */ }
        if (!ok && item.state !== 'removed') {
          item.state = 'failed';
          tile.classList.add('is-failed');
          if (!this.querySelector('[data-vfw-pu-error]').textContent) {
            this.say(this.getAttribute('fail-msg') || 'That photo did not upload — tap it to remove, then try again.');
          }
        }
        tile.querySelector('.vfw-pu__bar-fill').style.width = ok ? '100%' : '0%';
        tile.classList.toggle('is-done', ok);
        this.sync();
      };
      xhr.onerror = xhr.ontimeout = () => {
        if (item.state === 'removed') return;
        item.state = 'failed';
        tile.classList.add('is-failed');
        this.say(this.getAttribute('fail-msg') || 'That photo did not upload — tap it to remove, then try again.');
        this.sync();
      };
      xhr.send(payload);
      this.sync();
    }

    sync() {
      this.items = this.items.filter((i) => i.state !== 'removed');
      this.hiddenField.value = this.items.filter((i) => i.state === 'done').map((i) => i.url).join('\n');
      const full = this.items.filter((i) => i.state !== 'failed').length >= this.max;
      this.querySelector('[data-vfw-pu-add]').toggleAttribute('disabled', full);
    }
  }

  if (!customElements.get('vfw-photo-upload')) {
    customElements.define('vfw-photo-upload', VfwPhotoUpload);
  }
})();
